import { action, computed, observable, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { isSome } from 'core/utils';
import { walmartHandoff } from 'common/utils';

class WalmartConnectedViewStore extends BaseViewStore {
	@observable isRedirecting = false;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);

		// The connect attempt is over either way, so the handoff must not leak into the next one.
		walmartHandoff.clear();

		this.initialize();
	}

	/**
	 * On success the seller is handed straight to the channel they connected, with the outcome in a
	 * toast. This route is public so it renders the login page chrome, which reads as "you are
	 * signed out" to someone who is not. The card stays for the cases that cannot be handed off: a
	 * failure, an unknown channel, or a session that expired during Walmart's consent screens.
	 */
	@action.bound
	initialize() {
		if (!this.isSuccess || !isSome(this.channelId) || !this.isAuthenticated) {
			return;
		}

		this.isRedirecting = true;
		// ToastContainer sits above RouterView in AppTemplate, so a toast outlives the transition.
		// The store's push/pull mode is not an option here - nothing ever calls pull().
		this.rootStore.notificationStore.success(['OAUTH.WALMART.CONNECTED.SUCCESS']);
		this.goToChannel();
	}

	@computed
	get isAuthenticated() {
		return this.rootStore.authStore.isAuthenticated;
	}

	@computed
	get isSuccess() {
		return this.rootStore.routerStore.routerState.queryParams.status === 'success';
	}

	@computed
	get channelId() {
		const channelId = this.rootStore.routerStore.routerState.queryParams.channelId;
		return isSome(channelId) && channelId !== '' ? channelId : null;
	}

	/**
	 * Reason code from the callback, mapped to a translation key. Unknown codes fall back to a
	 * generic message rather than rendering a raw code from the query string.
	 */
	@computed
	get messageKey() {
		if (this.isSuccess) {
			return 'OAUTH.WALMART.CONNECTED.SUCCESS';
		}

		const reason = this.rootStore.routerStore.routerState.queryParams.reason;
		const knownReasons = [
			'state_expired',
			'invalid_type',
			'client_mismatch',
			'missing_seller',
			'missing_code',
			'channel_not_found',
			'org_mismatch',
			'wrong_channel_type',
			'seller_mismatch',
			'seller_already_connected',
			'exchange_failed',
			'unexpected_error',
		];

		return knownReasons.indexOf(reason) === -1
			? 'OAUTH.WALMART.CONNECTED.UNEXPECTED_ERROR'
			: `OAUTH.WALMART.CONNECTED.${reason.toUpperCase()}`;
	}

	goToChannel = () => {
		if (!isSome(this.channelId)) {
			return;
		}
		this.rootStore.routerStore.goTo('master.app.main.channel.edit', { id: this.channelId });
	};

	goToChannelList = () => {
		this.rootStore.routerStore.goTo('master.app.main.channel.list');
	};
}

export default WalmartConnectedViewStore;
